export interface GenerateVideoThumbnailOptions {
  /**
   * Seconds into the video to capture. Use a small non-zero value
   * (default 0.1s) — at exactly 0 some browsers haven't decoded a
   * frame yet and the canvas comes back blank.
   */
  captureAtSeconds?: number;
  /** MIME type for the output blob. Default `image/jpeg`. */
  outputType?: string;
  /** JPEG quality in [0, 1]. Default 0.9. */
  quality?: number;
}

export interface VideoThumbnail {
  /** The thumbnail image bytes. */
  blob: Blob;
  /** Natural width of the source video in pixels. */
  videoWidth: number;
  /** Natural height of the source video in pixels. */
  videoHeight: number;
}

/**
 * Capture a poster image from a video file. Mounts the video in an
 * off-DOM element, seeks to `captureAtSeconds`, paints the frame
 * onto a canvas, and returns the encoded blob plus the source video
 * dimensions.
 *
 * Mirrors PopshopRN's `generateVideoThumbnail` helper used by
 * `usePostMediaFileService` / `useGroupMessageMediaFileService`.
 *
 * Throws on:
 *   - canvas allocation failure
 *   - the browser failing to load / seek the video
 *   - `toBlob` returning null (out-of-memory; very rare)
 */
export async function generateVideoThumbnail(
  source: File | Blob | string,
  options: GenerateVideoThumbnailOptions = {},
): Promise<VideoThumbnail> {
  const {
    captureAtSeconds = 0.1,
    outputType = 'image/jpeg',
    quality = 0.9,
  } = options;

  const objectUrl =
    typeof source === 'string' ? source : URL.createObjectURL(source);
  const shouldRevokeUrl = typeof source !== 'string';

  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    // Prevent the browser from kicking off bandwidth-expensive
    // auto-buffering; we only need a single decoded frame.
    video.preload = 'metadata';
    // `crossOrigin` matches the image helpers — required for canvas
    // pixel read to succeed without tainting.
    video.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => resolve(), { once: true });
      video.addEventListener('error', () =>
        reject(new Error('Failed to load video metadata')),
      );
      video.src = objectUrl;
    });

    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      const onError = () => {
        video.removeEventListener('error', onError);
        reject(new Error('Failed to seek video to thumbnail frame'));
      };
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);
      video.currentTime = Math.min(captureAtSeconds, video.duration || 0);
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to create canvas context for thumbnail');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        encoded =>
          encoded
            ? resolve(encoded)
            : reject(new Error('Failed to encode video thumbnail')),
        outputType,
        quality,
      );
    });

    return {
      blob,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
    };
  } finally {
    if (shouldRevokeUrl) URL.revokeObjectURL(objectUrl);
  }
}
